// `MediaRecorder` provides three states: `inactive`, `paused`, and `recording`.
// We also have a `processing` state to represent the period after recording has stopped while the audio is being processed.
// Both `idle` and `processing` therefore correspond to a `MediaRecorder`state of `inactive`, since the recorder is no longer recording.

export const WOUNDSCRIBE_MIC_STATES = {
  IDLE: 'idle',
  RECORDING: 'recording',
  PAUSED: 'paused',
  PROCESSING: 'processing',
  INACTIVE: 'inactive',
};

export const WOUNDSCRIBE_AUDIO_BITS_PER_SECOND = 64000;

export const WOUNDSCRIBE_MAX_RECORDING_MINUTES = '75';

export const TOAST_DURATION_SHORT = 3000;
export const TOAST_DURATION_LONG = 5000;

//Web-socket constants

export const WOUNDSCRIBE_SOCKET_NAMESPACE = '/ambient-session';

export const WOUNDSCRIBE_SOCKET_EVENTS = {
  START_SESSION: 'start-session',
  AUDIO_CHUNK: 'audio-chunk',
  PAUSE_SESSION: 'pause-session',
  RESUME_SESSION: 'resume-session',
  STOP_SESSION: 'stop-session',
  TRANSCRIPTION: 'transcription',
  STATUS: 'status',
  ERROR: 'error',
} as const;

// IndexedDB durability store for audio chunks.

export const WOUNDSCRIBE_AUDIO_CHUNK_DB_NAME = 'woundscribe-recordings';
export const WOUNDSCRIBE_AUDIO_CHUNK_DB_VERSION = 1;
export const WOUNDSCRIBE_AUDIO_CHUNK_STORE_NAME = 'audio-chunks';

// Groups every chunk belonging to one continuous recording take —
// the primary correlation key for eviction and future retry, since it is
// generated client-side and exists even if the backend/socket never does.
export const WOUNDSCRIBE_AUDIO_CHUNK_RECORDING_INDEX = 'by-local-recording-id';
export const WOUNDSCRIBE_AUDIO_CHUNK_RECORDING_INDEX_KEY_PATH =
  'localRecordingId';

// Lets future recovery UI answer "what unfinished recording(s) exist for
// this encounter" — an encounter can have more than one local recording
// (e.g. a retake left over from a dropped connection).
export const WOUNDSCRIBE_AUDIO_CHUNK_ENCOUNTER_INDEX = 'by-encounter-id';
export const WOUNDSCRIBE_AUDIO_CHUNK_ENCOUNTER_INDEX_KEY_PATH = 'encounterId';

// Zero-pads chunkIndex within the primary key so key order matches capture
// order in IndexedDB's native (lexicographic) key sort and in DevTools.
// 6 digits covers ~69 hours of chunks at the 250ms capture interval, well
// beyond WOUNDSCRIBE_MAX_RECORDING_MINUTES.
export const WOUNDSCRIBE_AUDIO_CHUNK_INDEX_PAD_LENGTH = 6;

// Delay and number of retries to attempt to get the confirmation
// from backend if the audio is stored in s3
export const INITIAL_STATUS_CHECK_DELAY_MS = 2000;
export const MAX_STATUS_CHECK_RETRIES = 2;

export const SPEAKER_PREFIX_REGEX = /^Speaker[ \t]{1,10}([1-4]):/i;

export const MANUAL_AUDIO_TRANSCRIBE_POLL_MS = 2000;
export const MANUAL_AUDIO_TRANSCRIBE_MAX_ATTEMPTS = 90; // ~3 minutes

// Audio upload constants (must stay aligned with wound-scribe MANUAL_AUDIO_* limits)
export const WOUNDSCRIBE_MAX_UPLOAD_SIZE_BYTES = 50 * 1024 * 1024; // 50MB
export const WOUNDSCRIBE_ALLOWED_AUDIO_EXTENSIONS = [
  'mp3',
  'wav',
  'm4a',
  'mp4',
  'webm',
  'ogg',
];
export const WOUNDSCRIBE_ALLOWED_AUDIO_MIME_TYPES = [
  'audio/mpeg', // mp3
  'audio/mp3',
  'audio/wav',
  'audio/x-wav',
  'audio/wave',
  'audio/mp4', // m4a / mp4
  'audio/m4a',
  'audio/x-m4a',
  'video/mp4',
  'audio/webm',
  'video/webm',
  'audio/ogg',
  'application/ogg',
  'application/octet-stream', // some browsers omit a real audio MIME
];

/** FHIR DocumentReference often lands shortly after stop-session ack. */
export const ENCOUNTER_TRANSCRIPTS_RETRY_MS = 2000;

/** How often to re-check eligibility while the server reports an encounter as busy. */
export const ENCOUNTER_ELIGIBILITY_POLL_MS = 5000;

/**
 * Shown the moment an upload is accepted, before the server re-check returns. Matches the
 * reason the backend sends for an upload in progress.
 */
export const UPLOAD_PROCESSING_REASON_TEXT =
  'An uploaded recording for this encounter is still processing.';
